import { useEffect, useState } from "react";
import { Wand2, Loader2, Save, TrendingUp, HeartPulse, CircleDollarSign, Package } from "lucide-react";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STATUS_STYLES = {
  paid: "bg-emerald-50 text-emerald-700",
  pending: "bg-amber-50 text-amber-700",
  failed: "bg-red-50 text-red-700",
  expired: "bg-slate-100 text-slate-500",
};
const BUILD_STATUSES = ["in_production", "preview_ready", "revision_requested", "completed"];

const REQ_STATUS_STYLES = {
  requested: "bg-amber-50 text-amber-700",
  scope_approved: "bg-emerald-50 text-emerald-700",
  declined: "bg-red-50 text-red-700",
  purchased: "bg-brand-surface text-brand-text",
  closed: "bg-slate-100 text-slate-500",
};

function ManageRequest({ req, onSaved }) {
  const isSite = req.package_id === "business_site";
  const [price, setPrice] = useState(req.quoted_price ?? "");
  const [notes, setNotes] = useState(req.founder_notes || "");
  const [saving, setSaving] = useState(null);
  const patch = async (status) => {
    if (status === "scope_approved" && (!price || Number(price) <= 0)) {
      toast.error("Set a price before approving the scope."); return;
    }
    setSaving(status);
    try {
      await api.patch(`/founder/build-requests/${req.request_id}`, {
        status, quoted_price: price === "" ? undefined : Number(price), founder_notes: notes,
      });
      toast.success("Request updated."); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save."); }
    finally { setSaving(null); }
  };
  return (
    <div className="rounded-xl border border-brand-line bg-white p-4" data-testid={`manage-request-${req.request_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium">{req.package_name} · <span className="text-slate-400">{req.email || req.user_id}</span></p>
        <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${REQ_STATUS_STYLES[req.status] || "bg-slate-100 text-slate-500"}`}>{req.status.replace("_", " ")}</span>
      </div>
      {req.message && <p className="mt-2 rounded-lg bg-brand-surface/40 p-3 text-sm text-slate-600" data-testid={`request-message-${req.request_id}`}>{req.message}</p>}
      {isSite && (
        <div className="mt-3">
          <label className="text-xs font-medium text-slate-500">Final price (USD)
            <input data-testid={`req-price-${req.request_id}`} type="number" min="0" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="e.g. 1199"
              className="mt-1 block w-full max-w-[180px] rounded-lg border border-brand-line px-3 py-2 text-sm" />
          </label>
        </div>
      )}
      <label className="mt-3 block text-xs font-medium text-slate-500">Note to customer
        <textarea data-testid={`req-notes-${req.request_id}`} value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
          className="mt-1 block w-full rounded-lg border border-brand-line px-3 py-2 text-sm" />
      </label>
      <div className="mt-3 flex flex-wrap gap-2">
        {isSite && req.status !== "purchased" && (
          <button data-testid={`approve-request-${req.request_id}`} onClick={() => patch("scope_approved")} disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-hover disabled:opacity-50">
            {saving === "scope_approved" ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Approve scope & set price
          </button>
        )}
        {req.status !== "purchased" && (
          <>
            <button data-testid={`save-request-${req.request_id}`} onClick={() => patch(req.status)} disabled={saving}
              className="inline-flex items-center gap-2 rounded-lg border border-brand-line px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50">
              {saving === req.status ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save note
            </button>
            <button data-testid={`decline-request-${req.request_id}`} onClick={() => patch("declined")} disabled={saving}
              className="inline-flex items-center gap-2 rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50">
              Decline
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function ManageBuild({ order, onSaved }) {
  const [status, setStatus] = useState(order.build_status || "in_production");
  const [preview, setPreview] = useState(order.preview_url || "");
  const [notes, setNotes] = useState(order.delivery_notes || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/founder/build-orders/${order.order_id}`, { build_status: status, preview_url: preview, delivery_notes: notes });
      toast.success("Order updated."); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save."); }
    finally { setSaving(false); }
  };
  return (
    <div className="rounded-xl border border-brand-line bg-white p-4" data-testid={`manage-${order.order_id}`}>
      <p className="text-sm font-medium">{order.package_name} · <span className="text-slate-400">{order.order_id}</span></p>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="text-xs font-medium text-slate-500">Build status
          <select data-testid={`status-${order.order_id}`} value={status} onChange={(e) => setStatus(e.target.value)}
            className="mt-1 block w-full rounded-lg border border-brand-line px-3 py-2 text-sm capitalize">
            {BUILD_STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
          </select>
        </label>
        <label className="text-xs font-medium text-slate-500">Preview URL
          <input data-testid={`preview-${order.order_id}`} value={preview} onChange={(e) => setPreview(e.target.value)} placeholder="https://…"
            className="mt-1 block w-full rounded-lg border border-brand-line px-3 py-2 text-sm" />
        </label>
      </div>
      <label className="mt-3 block text-xs font-medium text-slate-500">Note to customer
        <textarea data-testid={`notes-${order.order_id}`} value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
          className="mt-1 block w-full rounded-lg border border-brand-line px-3 py-2 text-sm" />
      </label>
      <button data-testid={`save-${order.order_id}`} onClick={save} disabled={saving}
        className="mt-3 inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-hover disabled:opacity-50">
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
      </button>
    </div>
  );
}

export default function FounderBuildOrders() {
  const [orders, setOrders] = useState(null);
  const [requests, setRequests] = useState([]);
  const [metrics, setMetrics] = useState(null);

  const load = () => {
    api.get("/founder/build-orders").then((r) => setOrders(r.data.orders)).catch(() => setOrders([]));
    api.get("/founder/build-requests").then((r) => setRequests(r.data.requests)).catch(() => setRequests([]));
    api.get("/founder/build-metrics").then((r) => setMetrics(r.data)).catch(() => setMetrics(null));
  };
  useEffect(() => { load(); }, []);

  const openRequests = requests.filter((r) => r.status !== "purchased" && r.status !== "closed");

  const paidCount = (orders || []).filter((o) => o.payment_status === "paid").length;
  const revenue = (orders || []).filter((o) => o.payment_status === "paid").reduce((s, o) => s + (o.amount || 0), 0);
  const m = metrics;

  return (
    <AppShell>
      <div className="mx-auto max-w-5xl px-8 py-10 ltifade" data-testid="founder-build-orders">
        <div className="flex items-center gap-2">
          <Wand2 size={20} className="text-brand" />
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Build Studio · Orders</h1>
        </div>
        <p className="mt-1 text-slate-500">Every Build Studio order and its payment status. Managed builds are delivered by your team.</p>

        {/* Revenue panel */}
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="build-metrics">
          <div className="rounded-xl border border-brand-line bg-white p-4">
            <div className="flex items-center gap-1.5 text-slate-400"><Package size={14} /><span className="text-xs">Builds sold</span></div>
            <p className="mt-1 font-heading text-2xl font-bold text-brand-deep" data-testid="metric-builds-sold">{m ? m.builds_sold : "—"}</p>
            {m && <p className="text-xs text-slate-400">+{m.blueprints_sold} blueprints</p>}
          </div>
          <div className="rounded-xl border border-brand-line bg-white p-4">
            <div className="flex items-center gap-1.5 text-slate-400"><CircleDollarSign size={14} /><span className="text-xs">Revenue (one-time)</span></div>
            <p className="mt-1 font-heading text-2xl font-bold text-brand-deep" data-testid="metric-revenue">${m ? m.total_revenue.toLocaleString() : revenue.toFixed(0)}</p>
            {m && <p className="text-xs text-slate-400">${m.builds_revenue.toLocaleString()} builds</p>}
          </div>
          <div className="rounded-xl border border-brand-line bg-white p-4">
            <div className="flex items-center gap-1.5 text-slate-400"><HeartPulse size={14} /><span className="text-xs">Active Care Plans</span></div>
            <p className="mt-1 font-heading text-2xl font-bold text-brand-deep" data-testid="metric-active-care">{m ? m.active_care_plans : "—"}</p>
            {m && <p className="text-xs text-slate-400">{m.by_plan?.launch_care ?? 0} Launch · {m.by_plan?.business_care ?? 0} Business{m.scheduled_cancellations ? ` · ${m.scheduled_cancellations} ending` : ""}</p>}
          </div>
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4">
            <div className="flex items-center gap-1.5 text-emerald-600"><TrendingUp size={14} /><span className="text-xs">MRR</span></div>
            <p className="mt-1 font-heading text-2xl font-bold text-emerald-700" data-testid="metric-mrr">${m ? m.mrr.toLocaleString() : "—"}</p>
            <p className="text-xs text-emerald-600/70">recurring / month</p>
          </div>
        </div>
        <p className="mt-1.5 text-xs text-slate-400">{orders?.length ?? 0} total orders · {paidCount} paid</p>

        {requests.length > 0 && (
          <div className="mt-8" data-testid="build-requests">
            <h2 className="font-heading text-lg font-medium">Build requests & quotes</h2>
            <p className="mt-1 text-sm text-slate-500">Approve a Business Site scope and set its price, or follow up on a Full Stack Beta quote. Approved scopes unlock payment for the customer.</p>
            <div className="mt-3 space-y-3">
              {(openRequests.length ? openRequests : requests).map((r) => (
                <ManageRequest key={r.request_id} req={r} onSaved={load} />
              ))}
            </div>
          </div>
        )}

        <div className="mt-8 overflow-hidden rounded-xl border border-brand-line bg-white" data-testid="build-orders-table">
          {orders === null ? (
            <p className="flex items-center justify-center gap-2 p-8 text-sm text-slate-500"><Loader2 size={15} className="animate-spin" /> Loading orders…</p>
          ) : orders.length === 0 ? (
            <p className="p-8 text-center text-sm text-slate-500">No build orders yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-brand-line bg-brand-surface/40 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr><th className="px-4 py-2.5">Package</th><th className="px-4 py-2.5">Amount</th><th className="px-4 py-2.5">Payment</th><th className="px-4 py-2.5">Created</th></tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.order_id} className="border-b border-brand-line last:border-0" data-testid={`order-row-${o.order_id}`}>
                    <td className="px-4 py-3 font-medium">{o.package_name}</td>
                    <td className="px-4 py-3">${o.amount} {o.currency?.toUpperCase()}</td>
                    <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_STYLES[o.payment_status] || "bg-slate-100 text-slate-500"}`}>{o.payment_status}</span></td>
                    <td className="px-4 py-3 text-slate-500">{new Date(o.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {orders?.some((o) => o.package_kind === "build" && o.payment_status === "paid") && (
          <div className="mt-8">
            <h2 className="font-heading text-lg font-medium">Manage managed builds</h2>
            <p className="mt-1 text-sm text-slate-500">Update status, add a preview link, and leave a note. Customers see this in Build Studio.</p>
            <div className="mt-3 space-y-3">
              {orders.filter((o) => o.package_kind === "build" && o.payment_status === "paid").map((o) => (
                <ManageBuild key={o.order_id} order={o} onSaved={load} />
              ))}
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
